import {AfterViewInit, ChangeDetectorRef, Component, EventEmitter, inject, Input, OnInit, Output} from '@angular/core';
import {ControlContainer, NgForm} from "@angular/forms";
import {BaseComponent, Guid, LeModalService, numberCompare} from "common-shared";
import {CopyPasteService, IImeFeatures, InstructionType, MasterInputInstructionModel, MasterOperationModel, ObjectTypeForCopy, ProcessSelectorModel, ProcessSelectorWithOperationsModel, StateService} from "web-shared";
import {IntegratedMasterService} from "./services/integrated-master.service";
import {ProcessService} from "../processes/process.service";
import {ShowByValidConditionPipe} from "./show-by-valid-condition.pipe";
import {takeUntil} from "rxjs/operators";
import {ProcessSelectorEditModalComponent} from "./process-selector-edit-modal/process-selector-edit-modal.component";
import {instanceToInstance} from "class-transformer";
import {ProcessEditModalComponent} from "../processes/process-edit-modal/process-edit-modal.component";
import {PropertyMappingEditorModalComponent} from "../../common/property-mapping/property-mapping-editor-modal/property-mapping-editor-modal.component";

@Component({
  selector: 'mes-web-integrated-master-editor',
  templateUrl: './integrated-master-editor.component.html',
  viewProviders: [{provide: ControlContainer, useExisting: NgForm}],
  styles: [],
  providers: [ShowByValidConditionPipe]
})
export class IntegratedMasterEditorComponent extends BaseComponent implements OnInit, AfterViewInit {
  @Input() masterPegsByRightOrderId: any[];
  @Input() readonly: boolean = false;
  settings = {
    Visibility: {
      ValidFromToDates: true,
      ProcessSelector: false,
      TaskSelector: false,
      Setup: true,
      Teardown: true,
      ValidExpressions: true,
      ProcessValidExpressions: true,
      TimeConstraints: true,
      OperationSplitSizeMax: true
    } as IImeFeatures
  };
  @Input() masterOperations: MasterOperationModel[];
  @Input() processSelectors: ProcessSelectorModel[] = [];
  @Output() processSelectorChanged = new EventEmitter<ProcessSelectorModel[]>();
  @Output() removeMasterOperation = new EventEmitter<MasterOperationModel>();
  @Output() masterOperationChanged = new EventEmitter<MasterOperationModel[]>();
  @Input() validConditionFilter: string = null;
  openedPanels: string[] = [null];
  @Output() onShowIme = new EventEmitter<MasterInputInstructionModel>();
  @Input() scrollIntoViewId: string;
  @Input() allowItemGroupsAsInputs: boolean = false;
  @Input() allowItemGroupsAsOutputs: boolean = false;
  @Input() allowResourceTemplate: boolean = true;
  @Input() isManufacturingItem: boolean = false;
  @Input() showPhantoms = true;
  nrOfColumns: number;
  InstructionType = InstructionType;
  expandedRowKeys: { [s: string]: boolean; } = {null: true, undefined: true};
  bodyTemplateName: string;

  public integratedMasterService: IntegratedMasterService = inject(IntegratedMasterService);
  public processService: ProcessService = inject(ProcessService);
  private stateService: StateService = inject(StateService);
  private modalService: LeModalService = inject(LeModalService);
  private changeDetector: ChangeDetectorRef = inject(ChangeDetectorRef);
  private showByValidConditionPipe: ShowByValidConditionPipe = inject(ShowByValidConditionPipe);
  public copyPasteService: CopyPasteService = inject(CopyPasteService);

  constructor() {
    super();
  }

  ngOnInit() {
    this.stateService.settings$.pipe(takeUntil(this.componentDestroyed)).subscribe(value => {
      this.settings.Visibility = value.Ime.Features;
      this.nrOfColumns = this.getNrOfColumns();
    });
    if (this.processSelectors?.length === 1) {
      this.openedPanels = [...this.openedPanels, this.getPanelId(this.processSelectors[0].Id)];

      let rowGroupKey = this.integratedMasterService.getRowGroupKey(this.processSelectors[0].Id, this.processSelectors);
      this.expandedRowKeys[rowGroupKey] = true;
    }
    this.calculateCountOfRowsInOperations();
    this.getBodyTemplateName();
  }

  ngAfterViewInit(): void {
    if (this.scrollIntoViewId) {
      this.scrollIntoView(this.scrollIntoViewId);
    }
  }

  copyProcessSelector(processSelector: ProcessSelectorModel, all: boolean = false) {
    const processSelectorsWithOperations: ProcessSelectorWithOperationsModel[] = [];
    if (all) {
      this.processSelectors.forEach(ps => {
        processSelectorsWithOperations.push(ProcessSelectorWithOperationsModel.convertTo(ps, this.masterOperations));
      });
    } else {
      processSelectorsWithOperations.push(ProcessSelectorWithOperationsModel.convertTo(processSelector, this.masterOperations));
    }
    this.copyPasteService.copy('ProcessSelector', processSelectorsWithOperations);
  }

  paste(type: ObjectTypeForCopy, masterOperationModel: MasterOperationModel, ps: ProcessSelectorModel, fromClipboard: boolean = false) {
    this.copyPasteService.paste('MasterOperation', null, ps, null, fromClipboard).then(result => {
      if (result?.type === 'MasterOperation') {
        this.addProcessesAfter(masterOperationModel, result.content as MasterOperationModel[]);
      }
      if (result?.type === 'ProcessSelector') {
        const processSelectors = (result.content as ProcessSelectorWithOperationsModel[]).map(value => value.ProcessSelector);
        this.addProcessSelectorAfter(ps, processSelectors);
        for (let processSelectorWithOperations of result.content as ProcessSelectorWithOperationsModel[]) {
          this.addProcessesAfter(null, processSelectorWithOperations.MasterOperations);
        }
      }
    });
  }

  openProcessEditModal(processId: string) {
    this.modalService.show(ProcessEditModalComponent, {initialState: {entityId: processId} as ProcessEditModalComponent, class: 'modal-xl'});
  }

  ensureAnyProcess() {
    this.integratedMasterService.ensureAnyLine(this.masterOperations, this.processSelectors);
    this.integratedMasterService.ensureRowGroupKeys({MasterOperations: this.masterOperations, ProcessSelectors: this.processSelectors});
    this.masterOperations = [...this.masterOperations];
    this.setChanged();
  }

  addProcess(ps: ProcessSelectorModel) {
    const masterOperationModels = this.masterOperations.filter(value => value.ProcessSelectorId === ps.Id);
    let masterOperationModel = new MasterOperationModel({ProcessSelectorId: ps.Id});
    if (masterOperationModels?.length) {
      masterOperationModel = masterOperationModels.sort((a, b) => numberCompare(a, b, obj => obj.ProcessNumber)).reverse()[0];
    }
    this.addProcessAfter(masterOperationModel);
  }

  addProcessesAfter(mo: MasterOperationModel, newMos: MasterOperationModel[]) {
    for (let newMo of newMos) {
      mo = this.addProcessAfter(mo, newMo);
    }
  }

  addProcessAfter(mo: MasterOperationModel = null, newMo: MasterOperationModel = null) {
    let index = -1;
    if (mo) {
      index = this.masterOperations.indexOf(mo);
    }
    mo ??= new MasterOperationModel();

    newMo = newMo ?? this.integratedMasterService.getNewMasterOperation(mo.ProcessSelectorId, mo.ProcessNumber + 10);
    if (newMo.MasterUseInstructions.length == 0) {
      this.integratedMasterService.addMasterUseInstructionAfter(newMo);
    }
    newMo.RowGroupKey = this.integratedMasterService.getRowGroupKey(newMo.ProcessSelectorId, this.processSelectors);
    this.masterOperations.splice(index + 1, 0, newMo);
    this.masterOperations = [...this.masterOperations];
    this.setChanged();
    return newMo;
  }

  addProcessSelectorAfter(ps: ProcessSelectorModel, processSelectorModels: ProcessSelectorModel[]) {
    for (let i = 0; i < processSelectorModels.length; i++) {
      let newPs = processSelectorModels[i];
      let index = this.processSelectors.indexOf(ps);
      if (ps?.Sequence) {
        newPs.Sequence = ps.Sequence + i + 1;
      }
      this.processSelectors.splice(index + 1, 0, newPs);
      this.processSelectors = [...this.processSelectors];
      this.openedPanels = [...this.openedPanels, this.getPanelId(newPs.Id)];
      this.processSelectorChanged.emit(this.processSelectors);
      this.setChanged();
    }
  }

  removeProcess(mo: MasterOperationModel) {
    this.masterOperations = [...this.masterOperations.filter(value => value !== mo)];
    this.setChanged();
  }

  removeProcesses(masterOperationModels: MasterOperationModel[]) {
    this.masterOperations = [...this.masterOperations.filter(value => masterOperationModels.every(mo => mo !== value))];
    this.setChanged();
  }

  editOperationItemProperties(mo: MasterOperationModel, mii?: MasterInputInstructionModel, ensureWeightProperty = false) {
    const initialState = this.integratedMasterService.editOperationItemProperties({mo: mo, mii: mii, ensureWeightProperty: ensureWeightProperty});
    const modalRef = this.modalService.show(PropertyMappingEditorModalComponent, {initialState, class: 'modal-xl'});
    modalRef.onHide$.pipe(takeUntil(this.componentDestroyed)).subscribe();
  }

  showIme(masterInputInstruction: MasterInputInstructionModel) {
    this.onShowIme.emit(masterInputInstruction);
  }

  setChanged() {
    this.masterOperationChanged.emit(this.masterOperations);
    this.calculateCountOfRowsInOperations();
    this.getBodyTemplateName();
    this.changeDetector.markForCheck();
  }

  addNewProcessSelector() {
    this.showProcessSelectorEditModal(new ProcessSelectorModel(), 'add');
  }

  showProcessSelectorEditModal(ps: ProcessSelectorModel, mode: 'add' | 'edit') {
    this.modalService.show(ProcessSelectorEditModalComponent, {initialState: {processSelector: ps, mode: mode, readonly: this.readonly, allowDeleteWithoutOperations: this.processSelectors.length < 2}, class: 'modal-xl'})
      .onHide$.pipe(takeUntil(this.componentDestroyed)).subscribe(value => {
      if (value.result === 'change') {
        if (mode === 'add') {
          if (this.processSelectors.length === 1 && this.processSelectors[0].Id === null) {
            ps.Id = Guid.create();
            this.processSelectors[0] = ps;
            for (const masterOperation of this.masterOperations) {
              masterOperation.ProcessSelectorId = ps.Id;
              masterOperation.RowGroupKey = this.integratedMasterService.getRowGroupKey(ps.Id, this.processSelectors);
            }
            this.masterOperations = [...this.masterOperations];
          } else {
            this.processSelectors = [...this.processSelectors, ps];
            const newMo = this.integratedMasterService.getNewMasterOperation(ps.Id);
            newMo.RowGroupKey = this.integratedMasterService.getRowGroupKey(ps.Id, this.processSelectors);
            this.integratedMasterService.addMasterUseInstructionAfter(newMo);
            this.masterOperations = [...this.masterOperations, newMo];
          }
        }
        this.openedPanels = [...this.openedPanels, this.getPanelId(ps.Id)];
        ps.ValidCondition = instanceToInstance(ps.ValidCondition);
        this.ensureViewUpdate();
      } else if (value.result === 'delete' || value.result === 'deleteWithOperations') {
        this.removeProcessSelector(value.result, ps.Id);
      }
    });
  }

  removeProcessSelector(action: 'deleteWithOperations' | 'delete', processSelectorId: string) {
    this.processSelectors = [...this.processSelectors.filter(processSelector => processSelector.Id !== processSelectorId)];
    if (this.processSelectors.length === 0) {
      this.processSelectors = [ProcessSelectorModel.createValid()];
      this.openedPanels = [...this.openedPanels, this.getPanelId(this.processSelectors[0].Id)];
    }
    if (action === 'delete') {
      for (const mo of this.masterOperations) {
        mo.ProcessSelectorId = (mo.ProcessSelectorId === processSelectorId) ? undefined : mo.ProcessSelectorId;
        mo.RowGroupKey = this.integratedMasterService.getRowGroupKey(processSelectorId, this.processSelectors);
      }
      this.masterOperations = [...this.masterOperations];
    } else if (action === 'deleteWithOperations') {
      this.masterOperations = [...this.masterOperations.filter(masterOperation => masterOperation.ProcessSelectorId !== processSelectorId)];
    }
    this.ensureViewUpdate();
  }

  removeAllProcessSelectors(action: 'deleteWithOperations' | 'delete') {
    const psIds = this.processSelectors.map(ps => ps.Id);
    psIds.forEach(psId => {
      this.removeProcessSelector(action, psId);
    });
  }

  private ensureViewUpdate() {
    this.processSelectors = [...this.processSelectors];
    this.processSelectorChanged.emit(this.processSelectors);
    this.setChanged();
  }

  changeProcessSelector(mo: MasterOperationModel, ps: ProcessSelectorModel) {
    mo.ProcessSelectorId = ps.Id;
    mo.RowGroupKey = this.integratedMasterService.getRowGroupKey(ps.Id, this.processSelectors);
    this.masterOperations = [...this.masterOperations];
    this.setChanged();
  }

  scrollIntoView(operationId: string): void {
    setTimeout(() => {
      const element = document.getElementById('operationId-' + operationId);
      if (element) {
        element.scrollIntoView({behavior: 'smooth'});
      }
    }, 500);
  }

  getPanelId = (Id: string) => 'panel-' + Id;

  getNrOfColumns() {
    const columnSettings = [
      this.settings.Visibility.ProcessValidExpressions,
      this.settings.Visibility.TaskSelector,
      this.settings.Visibility.Setup,
      this.settings.Visibility.Teardown,
      this.settings.Visibility.ValidExpressions,
      this.settings.Visibility.ValidFromToDates,
      this.settings.Visibility.TimeConstraints,
      this.settings.Visibility.TimeConstraints,
      this.settings.Visibility.TimeConstraints,
      this.settings.Visibility.OperationSplitSizeMax
    ];
    return 19 - columnSettings.filter(column => column === false).length;
  }

  calculateCountOfRowsInOperations() {
    this.masterOperations.forEach(mo => {
      const miisToShow = this.showByValidConditionPipe.transform(mo.MasterInputInstructions, this.validConditionFilter, this.showPhantoms, mo);
      const muisToShow = this.showByValidConditionPipe.transform(mo.MasterUseInstructions, this.validConditionFilter, this.showPhantoms, mo);
      const moisToShow = this.showByValidConditionPipe.transform(mo.MasterOutputInstructions, this.validConditionFilter, this.showPhantoms, mo);
      const nrOfInstructions = miisToShow.length + muisToShow.length + moisToShow.length;
      if (this.settings.Visibility.TaskSelector && mo.TaskSelectors.length) {
        const tssToShow = this.showByValidConditionPipe.transform(mo.TaskSelectors, this.validConditionFilter, this.showPhantoms);
        const tssWithoutInstruction = tssToShow.filter(ts => miisToShow.every(mii => mii.TaskSelectorId !== ts.Id)
          && muisToShow.every(mui => mui.TaskSelectorId !== ts.Id)
          && moisToShow.every(moi => moi.TaskSelectorId !== ts.Id));
        mo['RowCount'] =  tssWithoutInstruction.length + nrOfInstructions + 1;
      } else {
        mo['RowCount'] = nrOfInstructions + 1;
      }
    });
  }

  getCountOfRowsToShow(mo: MasterOperationModel) {
    this.calculateCountOfRowsInOperations();
    return mo['RowCount'];
  }

  private getBodyTemplateName() {
    this.bodyTemplateName = this.masterOperations[0]?.ProcessSelectorId ? 'rowexpansion' : 'body'
  }

  asMasterOperation(mo: any) {
    return mo as MasterOperationModel;
  }
}
